import { apiGet, apiPost, apiPut, apiRequest } from '@/utils/request';
import type {
  LeadBaseSetting,
  LeadBaseSettingPayload,
  LeadDialogueExample,
  LeadDialogueExamplePayload,
  LeadInvalidRule,
  LeadInvalidRulePayload,
  LeadPaginated,
  LeadQuestionPayload,
  LeadQuestionSortPayload,
  LeadQuestionSlot,
  LeadScenario,
  LeadScenarioCategory,
  LeadScenarioPayload,
  LeadStage,
} from '@/types/leadPrompt';

const remove = (url: string) => apiRequest(url, { method: 'DELETE' });

export const leadPromptApi = {
  base: {
    detail: (robotId: number, versionType: number) =>
      apiGet<LeadBaseSetting>('/lead/base-settings', {
        robot_id: robotId,
        version_type: versionType,
      }),
    save: (data: LeadBaseSettingPayload) =>
      apiPost<LeadBaseSetting>('/lead/base-settings/save', data),
  },
  questions: {
    list: (params: Record<string, unknown>) =>
      apiGet<LeadPaginated<LeadQuestionSlot>>('/lead/question-slots', params),
    create: (
      data: LeadQuestionPayload & {
        robot_id: number;
        version_type: number;
        lead_stage: LeadStage;
      },
    ) => apiPost<LeadQuestionSlot>('/lead/question-slots', data),
    update: (id: number, data: LeadQuestionPayload) =>
      apiPut<LeadQuestionSlot>(`/lead/question-slots/${id}`, data),
    delete: (id: number) => remove(`/lead/question-slots/${id}`),
    sort: (data: LeadQuestionSortPayload) => apiPut<[]>('/lead/question-slots-sort', data),
  },
  categories: {
    list: (robotId: number) =>
      apiGet<{ list: LeadScenarioCategory[] }>('/lead/scenario-categories', {
        robot_id: robotId,
      }),
    create: (robotId: number, name: string) =>
      apiPost<LeadScenarioCategory>('/lead/scenario-categories', { robot_id: robotId, name }),
    update: (id: number, name: string) =>
      apiPut<LeadScenarioCategory>(`/lead/scenario-categories/${id}`, { name }),
    delete: (id: number) => remove(`/lead/scenario-categories/${id}`),
  },
  scenarios: {
    list: (params: Record<string, unknown>) =>
      apiGet<LeadPaginated<LeadScenario>>('/lead/scenarios', params),
    create: (
      data: LeadScenarioPayload & {
        robot_id: number;
        version_type: number;
        lead_stage: LeadStage;
      },
    ) => apiPost<LeadScenario>('/lead/scenarios', data),
    update: (id: number, data: LeadScenarioPayload) =>
      apiPut<LeadScenario>(`/lead/scenarios/${id}`, data),
    delete: (id: number) => remove(`/lead/scenarios/${id}`),
    setEnabled: (id: number, enabled: number) =>
      apiPut<{ id: number; enabled: number }>(`/lead/scenarios/${id}/enabled`, { enabled }),
  },
  examples: {
    list: (params: Record<string, unknown>) =>
      apiGet<LeadPaginated<LeadDialogueExample>>('/lead/dialogue-examples', params),
    create: (
      data: LeadDialogueExamplePayload & {
        robot_id: number;
        version_type: number;
        lead_stage: LeadStage;
      },
    ) => apiPost<LeadDialogueExample>('/lead/dialogue-examples', data),
    update: (id: number, data: LeadDialogueExamplePayload) =>
      apiPut<LeadDialogueExample>(`/lead/dialogue-examples/${id}`, data),
    delete: (id: number) => remove(`/lead/dialogue-examples/${id}`),
  },
  invalidRules: {
    list: (params: Record<string, unknown>) =>
      apiGet<LeadPaginated<LeadInvalidRule>>('/lead/invalid-rules', params),
    create: (
      data: LeadInvalidRulePayload & {
        robot_id: number;
        version_type: number;
      },
    ) => apiPost<LeadInvalidRule>('/lead/invalid-rules', data),
    update: (id: number, data: LeadInvalidRulePayload) =>
      apiPut<LeadInvalidRule>(`/lead/invalid-rules/${id}`, data),
    delete: (id: number) => remove(`/lead/invalid-rules/${id}`),
    setEnabled: (id: number, enabled: number) =>
      apiPut<{ id: number; enabled: number }>(`/lead/invalid-rules/${id}/enabled`, { enabled }),
  },
};
